import { type CSSProperties, type FormEvent, useEffect, useState } from 'react'; // CSSProperties/FormEvent are GUEST_AUTH (temporary)
import { guestSignIn } from '../lib/auth'; // GUEST_AUTH (temporary)
import { salesMailto } from '../lib/sales';
import { useAuth } from './AuthProvider';
import { BetaTag, Roundel } from './brand';

/** Friendly messages for the `?auth_error=` codes the Worker redirects back with. */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  not_staff: 'That account is not a Helium staff account. Sign in with your nova-labs.com email.',
  invalid_state: 'Sign-in expired or was interrupted. Please try again.',
  expired: 'Sign-in expired. Please try again.',
  token_exchange_failed: "Couldn't complete sign-in. Please try again.",
  denied: 'Sign-in was cancelled.',
};

const BUBBLES: [number, number][] = [
  [70, 60],
  [180, 120],
  [120, 240],
  [300, 90],
  [260, 280],
];

// GUEST_AUTH (temporary): the guest affordance matches the staff button's
// footprint (full-width, 52px, 16px/600) but stays outlined so staff SSO reads
// as the primary action. Shared by the closed toggle and the open submit.
const GUEST_BUTTON_STYLE: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  height: 52,
  borderRadius: 12,
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  border: '1px solid var(--border-1)',
  fontWeight: 600,
  fontSize: 16,
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export function Login({ bubbles }: { bubbles: boolean }) {
  const { signInWithStaffSSO } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // GUEST_AUTH (temporary): guest access-code sign-in — torn out with the feature.
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestCode, setGuestCode] = useState('');
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);

  // Surface a failed SSO round-trip: the Worker redirects back to /?auth_error=<code>.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('auth_error');
    if (!code) return;
    setError(AUTH_ERROR_MESSAGES[code] ?? 'Sign-in failed. Please try again.');
    window.history.replaceState(null, '', window.location.pathname); // clear so a refresh doesn't re-show it
  }, []);

  // GUEST_AUTH (temporary)
  async function submitGuest(e: FormEvent) {
    e.preventDefault();
    if (!guestCode.trim() || guestBusy) return;
    setGuestBusy(true);
    setGuestError(null);
    const err = await guestSignIn(guestCode.trim());
    if (err) {
      setGuestError(err);
      setGuestBusy(false);
      return;
    }
    window.location.replace('/'); // full reload → AuthProvider re-hydrates via /api/auth/me
  }

  return (
    <div
      style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--bg-1)' }}
      className="he-login"
    >
      {/* left: sign-in */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '48px clamp(32px, 7vw, 110px)',
          maxWidth: 620,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 56 }}>
          <Roundel size={36} />
          <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>helium</span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--he-purple)',
              border: '1px solid var(--border-1)',
              padding: '3px 9px',
              borderRadius: 999,
              marginLeft: 2,
            }}
          >
            AI
          </span>
          <BetaTag fontSize={12} />
        </div>
        <h1 style={{ fontSize: 'clamp(34px, 4vw, 46px)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
          Explore the network,
          <br />
          just by asking.
        </h1>
        <p style={{ fontSize: 17, color: 'var(--fg-2)', marginTop: 16, marginBottom: 34, lineHeight: 1.5, maxWidth: 420 }}>
          Sign in to ask Helium AI about Hotspots, earnings, coverage and expansion — answered with live data.
        </p>

        {error && <p style={{ color: '#C2410C', fontSize: 13.5, margin: '0 0 16px' }}>{error}</p>}

        <button
          type="button"
          onClick={signInWithStaffSSO}
          className="he-press"
          style={{
            width: '100%',
            maxWidth: 420,
            height: 52,
            borderRadius: 12,
            // Inverts with the theme via tokens: black-on-white in light,
            // white-on-dark in dark (a hardcoded black button vanishes on dark).
            background: 'var(--fg-1)',
            color: 'var(--bg-1)',
            border: 'none',
            fontWeight: 600,
            fontSize: 16,
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <Roundel size={20} /> Sign in with Google (staff)
        </button>

        {/* GUEST_AUTH (temporary): guest access-code sign-in for client demos. */}
        {!guestOpen ? (
          <button type="button" onClick={() => setGuestOpen(true)} className="he-press" style={{ ...GUEST_BUTTON_STYLE, marginTop: 12 }}>
            Continue as guest
          </button>
        ) : (
          <form onSubmit={submitGuest} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 420, marginTop: 12 }}>
            <input
              type="password"
              autoComplete="off"
              autoFocus
              placeholder="Enter your access code"
              value={guestCode}
              onChange={(e) => setGuestCode(e.target.value)}
              disabled={guestBusy}
              style={{
                width: '100%',
                height: 52,
                borderRadius: 12,
                border: '1px solid var(--border-1)',
                background: 'var(--bg-1)',
                color: 'var(--fg-1)',
                padding: '0 16px',
                fontSize: 16,
                fontFamily: 'inherit',
              }}
            />
            <button
              type="submit"
              className="he-press"
              disabled={guestBusy || !guestCode.trim()}
              style={{ ...GUEST_BUTTON_STYLE, opacity: guestBusy || !guestCode.trim() ? 0.55 : 1 }}
            >
              {guestBusy ? 'Signing in…' : 'Continue as guest'}
            </button>
          </form>
        )}
        {/* GUEST_AUTH (temporary) */}
        {guestError && <p style={{ color: '#C2410C', fontSize: 13.5, margin: '10px 0 0', maxWidth: 420 }}>{guestError}</p>}

        {/* Neither sign-in path admits a visitor with no staff account and no
            access code; this is what the page offers them instead. A mailto, so
            there is no endpoint to protect and no credential to hold. */}
        <p style={{ fontSize: 13.5, color: 'var(--fg-2)', margin: '18px 0 0', maxWidth: 420 }}>
          Interested in Helium for your venues?{' '}
          <a
            href={salesMailto('Sent from the Helium AI sign-in page.')}
            style={{ color: 'var(--he-purple)', fontWeight: 600, textDecoration: 'underline' }}
          >
            Talk to sales
          </a>
          .
        </p>
      </div>

      {/* right: photo hero with signal bubbles */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#111 url(/assets/photo-install-indoor.webp) center/cover',
        }}
        className="he-login-hero"
      >
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(95,38,252,.34), rgba(3,10,18,.5))' }} />
        {bubbles &&
          BUBBLES.map(([t, l], i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${t}px`,
                left: `${l}px`,
                width: 40 + i * 14,
                height: 40 + i * 14,
                borderRadius: 999,
                background: 'var(--veil-80)',
                filter: 'drop-shadow(0 8px 30px rgba(0,0,0,.15))',
              }}
            />
          ))}
        <div style={{ position: 'absolute', left: 48, right: 48, bottom: 56, color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', opacity: 0.8 }}>
            A network built by people
          </div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.05, marginTop: 10, textTransform: 'uppercase' }}>
            379K Hotspots.
            <br />
            One question away.
          </div>
        </div>
      </div>
    </div>
  );
}
